import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearTrails,
  fetchTrails,
  selecthasMoreTrails,
  selectTrails,
  selectTrailsLoading,
} from "../../../../store/slices/trails";
import AuditTrailHeader from "../audit-trail-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import ApiTrails from "../../../../api/trail";
import moment from "moment";
import { Body } from "../../../../components/main-body/body";
import classNames from "classnames";
import "../../../../components/main-body/body.css";
import { selectSettings } from "../../../../store/slices/settings";
import ApiSiteManagement from "../../../../api/site-managements";
import { selectUsers, fetchUsers } from "../../../../store/slices/user_management";
import { Tooltip } from "antd";

import { Drawer } from "../../../../components/drawer/drawer";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import { Form } from "antd";
import {
  FormFieldName,
} from "../../../../components/form/form";

const UsersTrailTable = ({ navigation }) => {
  const trails = useSelector(selectTrails);
  const hasMore = useSelector(selecthasMoreTrails);
  const isLoading = useSelector(selectTrailsLoading);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const settings = useSelector(selectSettings);
  const sitesEnabled = settings?.find((s) => s.key === "sitesEnable")?.value === true;
  const [sitesList, setSitesList] = useState([]);

  const [selectedTrail, setSelectedTrail] = useState(null);
  const [isDetailsOpen, setDetailsOpen] = useState(false);

  const fetchData = (currentPage = page) => {
    if (!isLoading) {
      setPage(currentPage + 1);
      dispatch(
        fetchTrails({
          page: currentPage,
          ...filters,
          createdAtEnd: filters.period?.[filters.period.length - 1]?.end,
          createdAtStart: filters.period?.[filters.period.length - 1]?.start,
        })
      );
    }
  };

  const updateFilterValue = (filter) => setFilters(filter);

  const getSites = async (deselect, query = "") => {
    return ApiSiteManagement.getSites({ query }).then(({ data }) => {
      const { sites } = data || {};
      setSitesList(sites?.map((c) => ({ id: c.id, name: c.name })));
    });
  };

  useEffect(() => {
    if (sitesEnabled) getSites();
  }, [settings, sitesEnabled]);

  useEffect(() => {
    dispatch(clearTrails());
    fetchData(1);
  }, [filters, dispatch]);

  const TruncatedCell = ({ value, limit = 56 }) => {
    if (!value) return <span className="trail-cell">N/A</span>;
    const full = String(value);
    const isLong = full.length > limit;
    if (!isLong) return <span className="trail-cell">{full}</span>;
    const short = `${full.slice(0, limit)}…`;

    return (
      <Tooltip title={full}>
        <span className="trail-cell">{short}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: t("trails.table.time"),
      dataIndex: "time",
      key: "time",
      render: (_, trail) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {`${moment(trail.createdAt).utc().format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"}
        </span>
      ),
      width: "20%",
    },
    {
      title: t("trails.table.email"),
      dataIndex: "email",
      key: "email",
      render: (_, trail) => <span>{trail.userEmail || "N/A"}</span>,
      width: "18%",
    },
    // LF360-2047: the role the user acted in; older records have none.
    {
      title: t("trails.table.user_role"),
      dataIndex: "userRole",
      key: "userRole",
      render: (_, trail) => <span>{trail.userRole || "N/A"}</span>,
      width: "10%",
    },
    {
      title: t("trails.table.updated_from"),
      dataIndex: "updated_from",
      key: "updated_from",
      render: (_, trail) => <TruncatedCell value={trail.valueFrom} />,
      width: "12%",
    },
    {
      title: t("trails.table.updated_to"),
      dataIndex: "updated_to",
      key: "updated_to",
      render: (_, trail) => <TruncatedCell value={trail.valueTo} />,
      width: "12%",
    },
    {
      title: t("trails.table.action"),
      dataIndex: "action",
      key: "action",
      render: (_, trail) => (
        <span style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          {trail.action || "N/A"}
        </span>
      ),
      width: "30%",
    },
  ];

  const actionTypes = [
    { id: "unsuccessfulLoginAttempt", name: "Unsuccessful login attempt" },
    { id: "userBlocked", name: "Blocked after unsuccessful login attempt" },
    { id: "userLogged", name: "Logged to the system using native Login" },
    { id: "userInvited", name: "Invited user" },
    { id: "createdEnrollmentField", name: "Created enrollmentField" },
    { id: "updatedEnrollmentField", name: "Updated enrollmentField" },
    { id: "updatedEnrollmentFieldOption", name: "Updated enrollmentFieldOption" },
    { id: "createEnrolmentData", name: "Create enrolment data" },
    { id: "updatedEnrolmentData", name: "Updated enrolment data" },
    { id: "deletedEnrolmentData", name: "Deleted enrolment data" },
    { id: "createdSetting", name: "Created setting" },
    { id: "updatedSetting", name: "Updated setting" },
    { id: "updatedSubjectAvatar", name: "Updated avatar" },
    { id: "updatedSubjectStatus", name: "Updated status for subject" },
    { id: "subjectDeactivated", name: "Subject deactivated" },
    { id: "subjectDeleted", name: "Subject deleted" },
    { id: "subjectActivated", name: "Subject activated" },
    { id: "invitedSubject", name: "Invited subject" },
    { id: "reinvitedSubject", name: "Re-invited subject" },
    { id: "updatedAvatar", name: "Updated avatar" },
    { id: "updatedRole", name: "Updated role" },
    { id: "updatedStatus", name: "Updated user status" },
    { id: "updatedFirstName", name: "Updated first name for user" },
    { id: "updatedLastName", name: "Updated last name for user" },
    { id: "activatedAccount", name: "Activated user account" },
    { id: "completedResetPassword", name: "Completed reset password procedure" },
    { id: "changedPassword", name: "Сhanged password" },
    { id: "userDeactivated", name: "User deactivated" },
    { id: "userDeleted", name: "User deleted" },
    { id: "userActivated", name: "User activated" },
    // LF360-2047 / 2048
    { id: "roleUpdated", name: "Updated role field" },
    { id: "rolePermissionGranted", name: "Granted permission to role" },
    { id: "rolePermissionRevoked", name: "Revoked permission from role" },
    { id: "roleDefaultsRestored", name: "Restored role defaults" },
    { id: "roleChangeFailed", name: "Failed role change" },
  ];

  const usersFromStore = useSelector(selectUsers) || [];
  const userList =
    usersFromStore.map((u) => ({ id: u.id, name: u.fullName })) || [];

  useEffect(() => {
    if (!usersFromStore?.length) {
      dispatch(fetchUsers());
    }
  }, [usersFromStore?.length, dispatch]);

  const filterItems = [
    { name: "Date peroid", id: "period", period: true },
    { name: "Action type", id: "actionType", children: actionTypes },
    ...(sitesEnabled ? [{ name: "Sites", id: "siteIds", children: sitesList }] : []),
    { name: "Users", id: "userIds", children: userList },
  ];

  const exportTrail = () => {
    ApiTrails.exportTrails();
  };

  const handleSelectTrail = (trail) => {
    setSelectedTrail(trail);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedTrail(null);
  };

  const TrailDetailsDrawer = ({ open, trail, onClose }) => {
    if (!trail) return null;

    const fmtTime = trail?.createdAt
      ? `${moment(trail.createdAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
      : "N/A";

    const Row = ({ label, children }) => (
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 24 }}>
        <FormFieldName text={`${label}:`} />
        <div style={{ marginTop: 2 }}>{children}</div>
      </div>
    );

    return (
      <Drawer open={open} size={720} closable={false} onClose={onClose} destroyOnHidden>
        <DrawerHeader
          title={<p style={{ fontSize: 16, margin: 0, fontWeight: 600 }}>Record details</p>}
          onClose={onClose}
          isEditing={false}
        />

        <Form>
          <Row label="ID"><span>{trail?.id || "N/A"}</span></Row>
          <Row label="Time"><span style={{ whiteSpace: "nowrap" }}>{fmtTime}</span></Row>
          <Row label="Email"><span>{trail?.userEmail || "N/A"}</span></Row>
          <Row label="User role"><span>{trail?.userRole || "N/A"}</span></Row>
          <Row label="Full name"><span>{trail?.userName || "N/A"}</span></Row>
          <Row label="Action"><span>{trail?.action || "N/A"}</span></Row>

          <div style={{ height: 8 }} />

          <Row label="Updated from">
            <div style={{ marginTop: 2, flex: 1, minWidth: 0 }}>
              <span style={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>{trail?.valueFrom ?? "N/A"}</span>
            </div>
          </Row>
          <Row label="Updated to">
            <div style={{ marginTop: 2, flex: 1, minWidth: 0 }}>
              <span style={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>{trail?.valueTo ?? "N/A"}</span>
            </div>
          </Row>
        </Form>
      </Drawer>
    );
  };

  return (
    <Body disabledPaddings={true}>
      <AuditTrailHeader
        t={t}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        exportTrail={exportTrail}
      />
      <div className={classNames("lf-content")}>
        <InfinityTable
          data={trails}
          fetchData={fetchData}
          hasMore={hasMore}
          tableColumns={columns}
          isLoading={isLoading}
          before={navigation}
          onHandleRowClick={handleSelectTrail}
        />
      </div>

      <TrailDetailsDrawer
        open={isDetailsOpen}
        trail={selectedTrail}
        onClose={closeDetails}
      />
    </Body>
  );
};

export default UsersTrailTable;
